// Web port of the mobile `PropertyPin`, a Leaflet divIcon shaped exactly like
// the app's marker: a round badge with an icon, a downward pointer whose tip
// rests on the geo-point, and an optional name/price chip above. Same colour
// theme as mobile: navy badge + white icon normally, gold badge + navy icon when
// selected (override per-marker with `color`, e.g. Crowd status colours).
import L from "leaflet";

export const PIN_NAVY = "#153568";
export const PIN_GOLD = "#FFA902";

// Minimal lucide-style stroke icons drawn inside the badge.
const ICONS = {
  home: '<path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><path d="M9 22V12h6v10"/>',
  doc: '<path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7z"/><path d="M14 2v5h5"/><path d="M9 13h6"/><path d="M9 17h6"/>',
  search:
    '<path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><path d="M14 2v6h6"/><circle cx="11.5" cy="14.5" r="2.5"/><path d="m15 18-1.5-1.5"/>',
};

const svgIcon = (key, color, px) =>
  `<svg width="${px}" height="${px}" viewBox="0 0 24 24" fill="none" stroke="${color}" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">${
    ICONS[key] || ICONS.home
  }</svg>`;

const esc = (s) =>
  String(s).replace(
    /[&<>"]/g,
    (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c])
  );

/**
 * Build the marker divIcon. Pair with the default Leaflet Marker (the returned
 * `iconAnchor` puts the pointer tip on the coordinate).
 * @param {object} o
 * @param {"home"|"doc"|"search"} o.icon
 * @param {string} o.label    name/price chip above the pin ("" = none)
 * @param {boolean} o.selected larger badge + gold accent
 * @param {string} o.color    badge fill override (e.g. status colour)
 * @param {string} o.iconColor icon stroke override
 * @param {string} o.labelColor chip text colour
 */
export function makePinIcon({
  icon = "home",
  label = "",
  selected = false,
  color,
  iconColor,
  labelColor = PIN_NAVY,
  ring, // optional outer ring colour, used to flag the user's own markers
} = {}) {
  const badge = color || (selected ? PIN_GOLD : PIN_NAVY);
  const fg = iconColor || (selected ? PIN_NAVY : "#ffffff");
  const size = selected ? 44 : 36;
  const iconPx = selected ? 22 : 18;
  const hasLabel = !!label;
  const labelBox = hasLabel ? 26 : 0; // chip height + margin
  const triH = 8;
  const w = hasLabel ? 170 : size;
  const h = labelBox + size + triH;

  const chip = hasLabel
    ? `<div style="margin-bottom:4px;max-width:160px;padding:3px 8px;background:#fff;border-radius:8px;box-shadow:0 2px 4px rgba(0,0,0,.25);font-size:12px;font-weight:700;color:${labelColor};white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">${esc(
        label
      )}</div>`
    : "";

  const shadow = ring
    ? `box-shadow:0 0 0 3px ${ring},0 2px 6px rgba(0,0,0,.35);`
    : "box-shadow:0 2px 5px rgba(0,0,0,.3);";
  const html = `<div style="display:flex;flex-direction:column;align-items:center;width:${w}px;">
      ${chip}
      <div style="width:${size}px;height:${size}px;background:${badge};border:2.5px solid #fff;border-radius:50%;${shadow}display:flex;align-items:center;justify-content:center;">
        ${svgIcon(icon, fg, iconPx)}
      </div>
      <svg width="16" height="10" viewBox="-1 -1 16 10" style="margin-top:-2px;display:block;"><path d="M0 0 L14 0 L7 8 Z" fill="${badge}" stroke="#ffffff" stroke-width="2"/></svg>
    </div>`;

  return L.divIcon({
    className: "jh-map-pin",
    html,
    iconSize: [w, h],
    iconAnchor: [w / 2, h], // pointer tip on the coordinate
    // The coordinate sits at the pointer TIP, so a `direction="top"` tooltip
    // would open on top of the badge. Lift its anchor to the icon's top edge
    // (above the name chip when there is one) so it floats clear of the pin.
    tooltipAnchor: [0, -h],
  });
}
